// After-call wrap-up in the dock (UX spec 6.2.10, SALE-08): the call this person was just on stays as a
// card for a short window, with its outcome, its note field and Done. The window ends on Done, when the
// next call starts or is answered, or once the card has been left alone for WRAP_UP_MS. There is no
// wrap-up policy in the contract yet (C13), so the card never blocks the dialer and sets no presence.
// Pure: no React.
import type { Call } from '@bcio-voice/contracts';
import { formatDuration } from '../../lib/format.ts';
import { outcomeLabel } from './callView.ts';

/** How long the card stays once it's left alone: the spec's example countdown ("Ready in 0:45"). */
export const WRAP_UP_MS = 45_000;
export const WRAP_UP_TICK_MS = 1000;

export type WrapUp = { callId: string; remainingMs: number; keptOpen: boolean } | null;

export type WrapUpEvent =
  /** A call this person was on (an active card in the dock) ended. The latest one wins. */
  | { type: 'ended'; callId: string }
  /** A new call started or was answered: the wrap-up is over. */
  | { type: 'next_call' }
  /** Time passed. Paused while the person is using the card or has an unsaved note in it (WCAG 2.2.1). */
  | { type: 'tick'; elapsedMs: number; paused: boolean }
  /** "Keep open": no countdown; only Done or the next call ends it. */
  | { type: 'keep_open' }
  | { type: 'done' };

export function wrapUpReducer(state: WrapUp, event: WrapUpEvent): WrapUp {
  switch (event.type) {
    case 'ended':
      return { callId: event.callId, remainingMs: WRAP_UP_MS, keptOpen: false };
    case 'next_call':
    case 'done':
      return null;
    case 'keep_open':
      return state && !state.keptOpen ? { ...state, keptOpen: true } : state;
    case 'tick': {
      if (!state || state.keptOpen || event.paused) return state;
      const remainingMs = state.remainingMs - Math.max(0, event.elapsedMs);
      return remainingMs > 0 ? { ...state, remainingMs } : null;
    }
    default:
      return state;
  }
}

/**
 * What changed in the dock's own calls since the last render: the call that ended (wrap-up starts) and
 * whether another call began. `before` and `now` are the IDs of the dock's active cards, newest first;
 * a call that leaves the dock without having ended (answered elsewhere, say) gets no wrap-up.
 */
export function wrapUpTransition(before: readonly string[], now: readonly string[], hasEnded: (callId: string) => boolean): { ended: string | null; started: boolean } {
  const ended = before.find(id => !now.includes(id) && hasEnded(id)) ?? null;
  const started = now.some(id => !before.includes(id));
  return { ended, started };
}

export function callHasEnded(call: Call | null | undefined): boolean {
  return !!call && (call.state === 'ended' || call.state === 'failed');
}

/**
 * A call leaves the dock as ended for the wrap-up when the server says so, or when this person pressed End here: End
 * takes the live card away at once (locallyEnded), before the server's ended event arrives, and most cold calls end
 * that way. Without this the wrap-up (and its CRM disposition buttons) never showed for them.
 */
export function endedForWrapUp(call: Call | null | undefined, locallyEnded: ReadonlySet<string> | null | undefined): boolean {
  return callHasEnded(call) || (!!call && !!locallyEnded?.has(call.id));
}

/** The outcome summary under the party name: "Answered. On hold 0:20." */
export function wrapUpSummary(call: Call): string {
  const hold = call.hold_ms > 0 ? ` On hold ${formatDuration(call.hold_ms)}.` : '';
  const talk = call.talk_ms > 0 ? '' : ' No talk time.';
  return `${outcomeLabel(call)}.${talk}${hold}`;
}

export type WrapUpPause = 'note' | 'using' | null;

/** The countdown line. Shown in words, never announced (it changes every second). */
export function wrapUpCountdown(state: NonNullable<WrapUp>, pause: WrapUpPause): string {
  if (state.keptOpen) return 'Stays open until you press Done.';
  if (pause === 'note') return 'Stays open while your note isn’t saved.';
  const left = formatDuration(Math.ceil(state.remainingMs / 1000) * 1000);
  return pause === 'using' ? `Closes in ${left}. Paused while you’re using it.` : `Closes in ${left}.`;
}

interface NoteFieldScope {
  getElementById: (id: string) => Element | null;
  querySelectorAll: (selector: string) => ArrayLike<Element>;
}

const shown = (el: Element | null | undefined): el is HTMLElement => !!el && typeof (el as HTMLElement).focus === 'function' && el.getClientRects().length > 0;

/**
 * Alt+Shift+N (UX spec 6.2.6): the note field of the live call's card, else the wrap-up card's, else the
 * open call page's "Add a note". Only fields on screen count: on a phone the dock is hidden off the
 * Phone page, so the call page's field is the one to go to there.
 */
export function noteField(scope: NoteFieldScope, activeCallId: string | null): HTMLElement | null {
  const live = activeCallId ? scope.getElementById(`call-note-${activeCallId}`) : null;
  if (shown(live)) return live;
  for (const selector of ['textarea[id^="call-note-"]', 'textarea[id^="note-new-"]']) {
    const found = Array.from(scope.querySelectorAll(selector)).find(shown);
    if (found) return found;
  }
  return null;
}
